import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Icon } from "@iconify/react";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import PageHeader from "../components/common/PageHeader";
import LocationHistoryModal from "../components/LocationHistoryModal";
import FloatingFormActions from "../components/common/FloatingFormActions";

const DEFAULT_CENTER = { lat: 48.1351, lng: 11.5820 };

const TABS = [
  { id: "Identity", icon: "solar:shop-2-linear" },
  { id: "Address", icon: "solar:map-point-linear" },
  { id: "Opening Hours", icon: "solar:clock-circle-linear" },
  { id: "Ratings & Details", icon: "solar:star-fall-linear" },
  { id: "Raw Payload", icon: "solar:code-square-linear" },
];

const formatGoogleHours = (regularHours) => {
  const formatted = {
    monday: "Closed",
    tuesday: "Closed",
    wednesday: "Closed",
    thursday: "Closed",
    friday: "Closed",
    saturday: "Closed",
    sunday: "Closed",
  };

  if (!regularHours) return formatted;

  // Handle weekday_text array (e.g. ["Monday: 9:00 AM – 5:00 PM", ...])
  if (Array.isArray(regularHours.weekday_text)) {
    regularHours.weekday_text.forEach((text) => {
      const parts = text.split(":");
      const day = parts[0]?.trim().toLowerCase();
      const hours = parts.slice(1).join(":").trim();
      if (day && formatted.hasOwnProperty(day)) {
        formatted[day] = hours || "Closed";
      }
    });
    return formatted;
  }

  const periods = regularHours.periods || (Array.isArray(regularHours) ? regularHours : []);

  const parseTime = (timeVal) => {
    if (!timeVal) return "00:00";
    if (typeof timeVal === "object") {
      const h = String(timeVal.hours ?? timeVal.hour ?? 0).padStart(2, "0");
      const m = String(timeVal.minutes ?? timeVal.minute ?? 0).padStart(2, "0");
      return `${h}:${m}`;
    }
    const str = String(timeVal);
    if (str.includes(":")) return str;
    const padded = str.padStart(4, "0");
    return `${padded.slice(0, 2)}:${padded.slice(2, 4)}`;
  };

  const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

  periods.forEach((p) => {
    let dayStr = "";
    if (typeof p.open?.day === "number") {
      dayStr = dayNames[p.open.day] || "";
    } else if (p.openDay || p.day) {
      dayStr = String(p.openDay || p.day).toLowerCase();
    }

    if (dayStr && formatted.hasOwnProperty(dayStr)) {
      const open = parseTime(p.open?.time || p.openTime);
      const close = parseTime(p.close?.time || p.closeTime);
      formatted[dayStr] = `${open} - ${close}`;
    }
  });

  return formatted;
};

const LocationsGooglePlacesDataFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();

  const [activeTab, setActiveTab] = useState("Identity");
  const [loading, setLoading] = useState(true);
  const [placeData, setPlaceData] = useState(null);
  const [historyEndpoint, setHistoryEndpoint] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await api.get(`/google-places-data/${id}`);
      if (res.data?.success) {
        setPlaceData(res.data.data);
      }
    } catch (err) {
      showToast("error", "Failed to load Google Places data details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-80 bg-base radius-12 shadow-sm">
        <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl" />
      </div>
    );
  }

  if (!placeData) {
    return (
      <div className="p-32 text-center">
        <h5>Google Place data not found.</h5>
        <button className="btn btn-primary-600 mt-16" onClick={() => navigate("/google-places-data")}>
          Back to Google Places Data
        </button>
      </div>
    );
  }

  const rawPayload =
    typeof placeData.raw_payload === "string"
      ? JSON.parse(placeData.raw_payload || "{}")
      : placeData.raw_payload || {};

  const regularHours =
    typeof placeData.regular_hours === "string"
      ? JSON.parse(placeData.regular_hours || "{}")
      : placeData.regular_hours || {};

  const types =
    typeof placeData.types === "string"
      ? JSON.parse(placeData.types || "[]")
      : placeData.types || [];

  const attributes =
    typeof placeData.attributes === "string"
      ? JSON.parse(placeData.attributes || "{}")
      : placeData.attributes || {};

  const pos = {
    lat: Number(placeData.latitude) || DEFAULT_CENTER.lat,
    lng: Number(placeData.longitude) || DEFAULT_CENTER.lng,
  };

  const currentTabIdx = TABS.findIndex((t) => t.id === activeTab);
  const prevTab = currentTabIdx > 0 ? TABS[currentTabIdx - 1] : null;
  const nextTab = currentTabIdx >= 0 && currentTabIdx < TABS.length - 1 ? TABS[currentTabIdx + 1] : null;

  return (
    <>
      <div className="form-has-floating-actions">
      {/* Back Button */}
      <button
        type="button"
        onClick={() => navigate("/google-places-data")}
        className="btn btn-link p-0 text-secondary-light text-decoration-none d-inline-flex align-items-center gap-2 mb-16 hover-text-primary-600 transition-all"
      >
        <Icon icon="solar:arrow-left-linear" className="text-xl" />
        <span className="fw-medium text-sm">Back to Google Places Data</span>
      </button>

      {/* Header Section with History Button */}
      <PageHeader
        title={`View Place: ${placeData.title || "Google Place"}`}
        actions={
          <div className="d-flex align-items-center gap-2">
            {placeData.maps_uri && (
              <a
                href={placeData.maps_uri}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 px-16 py-10 radius-8 fw-semibold"
              >
                <Icon icon="solar:map-arrow-square-linear" className="text-xl" />
                <span className="text-sm">Open in Google Maps</span>
              </a>
            )}
            <button
              type="button"
              onClick={() => setHistoryEndpoint(`/google-places-data/${id}/history`)}
              className="btn btn-outline-primary d-inline-flex align-items-center gap-2 px-16 py-10 radius-8 fw-semibold"
            >
              <Icon icon="solar:history-linear" className="text-xl" />
              <span className="text-sm">View History</span>
            </button>
          </div>
        }
      />

      <div className="card radius-12 border-0 shadow-none bg-base overflow-hidden">
        <div className="d-flex flex-column flex-lg-row">
          {/* Navigation Sidebar */}
          <div className="bg-neutral-50 border-end" style={{ minWidth: "260px" }}>
            <div className="p-16">
              <div className="d-flex flex-column gap-1">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`d-flex align-items-center gap-3 px-16 py-12 radius-8 border-0 transition-all text-start w-100 ${
                      activeTab === tab.id
                        ? "bg-primary-600 text-white shadow-sm fw-bold"
                        : "bg-transparent text-secondary-light hover-bg-neutral-200"
                    }`}
                  >
                    <Icon
                      icon={tab.icon}
                      className={`text-xl ${activeTab === tab.id ? "text-white" : "text-primary-600"}`}
                    />
                    <span className="text-sm">{tab.id}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Read-Only Form Content */}
          <div className="flex-grow-1 p-32 p-lg-40" style={{ minHeight: "70vh" }}>
            <h5 className="fw-bold text-primary-light mb-32 border-bottom pb-16">{activeTab}</h5>

            {activeTab === "Identity" && (
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label text-xs fw-bold text-secondary-light">PLACE TITLE / NAME</label>
                  <input className="form-control bg-neutral-100" readOnly value={placeData.title || "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-xs fw-bold text-secondary-light">STORE CODE</label>
                  <input className="form-control bg-neutral-100" readOnly value={placeData.store_code || "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-xs fw-bold text-secondary-light">GOOGLE PLACE ID</label>
                  <input className="form-control bg-neutral-100 font-monospace text-xs" readOnly value={placeData.google_place_id || "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-xs fw-bold text-secondary-light">PRIMARY PHONE</label>
                  <input className="form-control bg-neutral-100" readOnly value={placeData.primary_phone || "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-xs fw-bold text-secondary-light">WEBSITE URI</label>
                  <input className="form-control bg-neutral-100" readOnly value={placeData.website_uri || "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-xs fw-bold text-secondary-light">LAST SYNCED AT</label>
                  <input
                    className="form-control bg-neutral-100"
                    readOnly
                    value={placeData.last_synced_at ? new Date(placeData.last_synced_at).toLocaleString() : "—"}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label text-xs fw-bold text-secondary-light">DESCRIPTION</label>
                  <textarea className="form-control bg-neutral-100" rows="3" readOnly value={placeData.description || "—"} />
                </div>
              </div>
            )}

            {activeTab === "Address" && (
              <div className="row g-4">
                <div className="col-lg-5">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label text-xs fw-bold text-secondary-light">FORMATTED ADDRESS</label>
                      <input className="form-control form-control-sm bg-neutral-100" readOnly value={placeData.address_line1 || "—"} />
                    </div>
                    <div className="col-8">
                      <label className="form-label text-xs fw-bold text-secondary-light">STREET</label>
                      <input className="form-control form-control-sm bg-neutral-100" readOnly value={placeData.street_address || "—"} />
                    </div>
                    <div className="col-4">
                      <label className="form-label text-xs fw-bold text-secondary-light">HOUSE NO.</label>
                      <input className="form-control form-control-sm bg-neutral-100" readOnly value={placeData.house_number || "—"} />
                    </div>
                    <div className="col-6">
                      <label className="form-label text-xs fw-bold text-secondary-light">CITY</label>
                      <input className="form-control form-control-sm bg-neutral-100" readOnly value={placeData.city || "—"} />
                    </div>
                    <div className="col-6">
                      <label className="form-label text-xs fw-bold text-secondary-light">STATE</label>
                      <input className="form-control form-control-sm bg-neutral-100" readOnly value={placeData.state || "—"} />
                    </div>
                    <div className="col-6">
                      <label className="form-label text-xs fw-bold text-secondary-light">POSTAL CODE</label>
                      <input className="form-control form-control-sm bg-neutral-100" readOnly value={placeData.postal_code || "—"} />
                    </div>
                    <div className="col-6">
                      <label className="form-label text-xs fw-bold text-secondary-light">COUNTRY</label>
                      <input className="form-control form-control-sm bg-neutral-100" readOnly value={placeData.country_code || "—"} />
                    </div>
                  </div>
                </div>
                <div className="col-lg-7">
                  <div className="border radius-12 overflow-hidden shadow-sm h-100" style={{ minHeight: "350px" }}>
                    {isLoaded ? (
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "100%" }}
                        center={pos}
                        zoom={16}
                        mapTypeId="hybrid"
                        options={{ mapTypeId: "hybrid", mapTypeControl: true }}
                      >
                        <MarkerF position={pos} />
                      </GoogleMap>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100 bg-neutral-100 text-secondary-light">
                        Loading Map Preview...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "Opening Hours" && (() => {
              const parsedHours = formatGoogleHours(regularHours);
              return (
                <div className="bg-neutral-50 p-24 radius-12 border">
                  <div className="row g-3">
                    {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => (
                      <div className="col-md-6 d-flex align-items-center gap-3 bg-white p-12 radius-8 border shadow-sm" key={day}>
                        <label className="fw-bold text-sm text-primary-600 mb-0 text-capitalize" style={{ width: "100px" }}>
                          {day}
                        </label>
                        <input
                          className="form-control border-0 bg-neutral-50 text-sm fw-medium"
                          readOnly
                          value={parsedHours[day] || "Closed"}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {activeTab === "Ratings & Details" && (
              <div className="row g-4">
                <div className="col-md-4">
                  <label className="form-label text-xs fw-bold text-secondary-light">RATING</label>
                  <div className="d-flex align-items-center gap-2 p-10 bg-neutral-100 radius-8">
                    <Icon icon="solar:star-bold" className="text-warning-main text-xl" />
                    <span className="fw-bold text-lg">{placeData.rating || "N/A"}</span>
                    <span className="text-xs text-secondary-light">({placeData.user_ratings_total || 0} reviews)</span>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label text-xs fw-bold text-secondary-light">BUSINESS STATUS</label>
                  <input className="form-control bg-neutral-100" readOnly value={placeData.business_status || "OPERATIONAL"} />
                </div>
                <div className="col-md-4">
                  <label className="form-label text-xs fw-bold text-secondary-light">OPEN STATUS</label>
                  <input className="form-control bg-neutral-100" readOnly value={placeData.open_status || "OPEN"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-xs fw-bold text-secondary-light">PRIMARY CATEGORY</label>
                  <input className="form-control bg-neutral-100" readOnly value={placeData.primary_category_name || "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-xs fw-bold text-secondary-light">PLACE TYPES</label>
                  <div className="d-flex flex-wrap gap-2 p-8 bg-neutral-100 radius-8" style={{ minHeight: "44px" }}>
                    {types.length > 0 ? (
                      types.map((t, idx) => (
                        <span key={idx} className="badge bg-primary-50 text-primary-600 border border-primary-200 radius-4 text-xs">
                          {t}
                        </span>
                      ))
                    ) : (
                      <span className="text-secondary-light text-xs">—</span>
                    )}
                  </div>
                </div>
                {attributes.wheelchair_accessible_entrance !== undefined && (
                  <div className="col-12">
                    <label className="form-label text-xs fw-bold text-secondary-light">ACCESSIBILITY</label>
                    <div className="p-12 bg-neutral-100 radius-8 text-sm">
                      Wheelchair Accessible Entrance: {attributes.wheelchair_accessible_entrance ? "Yes" : "No / Unknown"}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "Raw Payload" && (
              <div className="bg-neutral-50 p-16 radius-12 border">
                <pre className="font-monospace text-xs mb-0 bg-white p-16 radius-8 border" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {JSON.stringify(rawPayload, null, 2)}
                </pre>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* STICKY BOTTOM ACTION BAR */}
        <FloatingFormActions
          prevStep={prevTab?.id}
          onPrev={prevTab ? () => setActiveTab(prevTab.id) : null}
          nextStep={nextTab?.id}
          onNext={nextTab ? () => setActiveTab(nextTab.id) : null}
          cancelUrl="/google-places-data"
          cancelText="Back to Places"
          hideSave={true}
          statusBadge={
            <span className="badge bg-primary-50 text-primary-600 border border-primary-200 px-10 py-6 radius-6 text-xs d-flex align-items-center gap-1">
              <Icon icon="solar:shop-2-bold" /> {placeData.business_status || "OPERATIONAL"}
            </span>
          }
        />
      </div>

      {/* History Modal */}
      {historyEndpoint && (
        <LocationHistoryModal
          endpoint={historyEndpoint}
          onClose={() => setHistoryEndpoint(null)}
        />
      )}
    </>
  );
};

export default LocationsGooglePlacesDataFormPage;

